import React, { useCallback, useEffect, useState } from 'react';
import deltaService, { fmtM, fmtPct } from '../../services/deltaService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';
import { useAuth } from '../../context/AuthContext';
import { AVAIL, Chip, Empty, Label, PillGroup, Section, Tile } from './DeltaUI';

// The position register and its inventory pipeline.
//
//   requested → submitted → validated → classified → signed off
//
// The ladder is climbed one rung at a time and may fall back to any earlier rung. The
// server enforces that; this screen only ever offers the next rung and the fallbacks, so
// a user is never shown a control that will be refused.
//
// Only signed-off positions count toward Distributable Liquidity, which is why the
// pipeline is a first-class screen rather than a status field buried in an edit form.

const PIPELINE = ['requested', 'submitted', 'validated', 'classified', 'signed_off'];
const PIPELINE_LABEL = {
  requested: 'Requested',
  submitted: 'Submitted',
  validated: 'Validated',
  classified: 'Classified',
  signed_off: 'Signed off',
};
const STAGE_CHIP = {
  requested: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
  submitted: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  validated: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  classified: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400',
  signed_off: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
};

const STATUS_FILTERS = [{ value: '', label: 'All stages' },
  ...PIPELINE.map((s) => ({ value: s, label: PIPELINE_LABEL[s] }))];

const BOOKS = [
  { value: '', label: 'All books' },
  { value: 'A', label: 'Core' },
  { value: 'B', label: 'Income' },
  { value: 'C', label: 'Run-off' },
  { value: 'D', label: 'Retained' },
];

export default function InventoryPage() {
  const { canWrite, canApprove } = useAuth();
  const [positions, setPositions] = useState([]);
  const [figures, setFigures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [book, setBook] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      deltaService.getPositions({ inventory_status: status || undefined, book: book || undefined }),
      deltaService.getFigures().catch(() => null),
    ]).then(([p, f]) => {
      setPositions(Array.isArray(p) ? p : []);
      setFigures(f);
    }).catch(() => setPositions([])).finally(() => setLoading(false));
  }, [status, book]);

  useEffect(() => { load(); }, [load]);

  const transition = async (position, target) => {
    setError('');
    try {
      await deltaService.transitionPosition(position.id, target);
      load();
    } catch (err) {
      // The server's refusal messages state the governance rule that blocked the move —
      // show them verbatim rather than a generic failure.
      setError(err?.response?.data?.detail || 'The change could not be made.');
    }
  };

  const completeness = figures?.inventory_completeness;

  return (
    <div>
      {/* What this screen is, in two sentences. It is the only screen here that is a
          working tool rather than a report, and nobody arriving at it cold could tell that
          from a grid of stage counts. */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-gray-800 dark:bg-[#0d1117] sm:px-4">
        <h2 className="font-serif text-[17px] leading-tight text-gray-900 dark:text-gray-50">
          The asset register
        </h2>
        <p className="mt-1 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400">
          {/* Says what is held, not whose it is — see DELTA_INTRO in content/deck.js. */}
          Every asset on the register, and how far each one has got through checking. An
          asset is entered, its paperwork submitted, its value validated, its book and
          liquidity classified, and only then signed off. Nothing counts towards the
          Dashboard&rsquo;s figures until it has been signed off — which is why this screen
          exists rather than a status field hidden in an edit form.
        </p>
      </div>

      {/* Labelled in plain words. "Signed off, by count" and "by value" were precise and
          meaningless to anyone not already holding both denominators in their head. */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <Tile
          tone={completeness?.is_complete ? 'ok' : 'warn'}
          label="Assets checked"
          value={completeness ? `${completeness.signed_off_count} of ${completeness.total_count}` : '—'}
          sub="Fully checked and signed off"
        />
        <Tile
          tone={completeness?.is_complete ? 'ok' : 'warn'}
          label="Value those cover"
          value={fmtPct(completeness?.by_value)}
          sub="Share of the book held in checked assets. A low figure here beside a high count means the large positions are still outstanding."
        />
        <Tile label="Total on the register" value={fmtM(completeness?.total_value)} sub="AED million, every stage" />
        <Tile label="Assets entered" value={completeness?.total_count ?? '—'} sub="However far through checking" />
      </div>

      {/* Stage counts double as a progress read on the pipeline itself. */}
      <div className="scrollbar-thin -mx-3 mb-4 flex gap-2 overflow-x-auto px-3 sm:mx-0 sm:px-0">
        {PIPELINE.map((s) => (
          <div key={s} className="min-w-[92px] shrink-0 rounded border border-gray-200 px-2.5 py-2 dark:border-gray-800">
            <Label>{PIPELINE_LABEL[s]}</Label>
            <div className="mt-0.5 font-serif text-lg text-gray-900 dark:text-gray-100">
              {completeness?.by_status?.[s] ?? '—'}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-4 grid gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-[#0d1117] sm:grid-cols-2 sm:p-4">
        <PillGroup label="Stage" options={STATUS_FILTERS} value={status} onChange={setStatus} />
        <PillGroup label="Book" options={BOOKS} value={book} onChange={setBook} />
      </div>

      {error && (
        <div className="mb-4 rounded border-l-2 border-rose-500 bg-rose-50 px-3 py-2.5 text-[12px] leading-relaxed text-rose-900 dark:bg-rose-500/10 dark:text-rose-200">
          {error}
        </div>
      )}

      <Section
        title="Position register"
        subtitle={`${positions.length} shown${status || book ? ' matching the filters' : ''}`}
        onRerun={load}
        busy={loading}
      >
        <RegisterTable
          rows={positions}
          loading={loading}
          canWrite={canWrite}
          canApprove={canApprove}
          onTransition={transition}
        />
      </Section>

      <p className="px-1 pb-2 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
        Sign-off is reserved to the Shareholder. Only signed-off positions count toward
        Distributable Liquidity.
      </p>
    </div>
  );
}

const COLS = [
  { key: 'name', type: 'string', get: (r) => r.name },
  { key: 'book', type: 'string', get: (r) => r.book_display },
  { key: 'stage', type: 'number', get: (r) => PIPELINE.indexOf(r.inventory_status) },
  { key: 'value', type: 'number', get: (r) => r.carrying_value },
  { key: 'free', type: 'number', get: (r) => r.free_value },
];

function RegisterTable({ rows, loading, canWrite, canApprove, onTransition }) {
  const { sorted, toggle, indicator } = useSortableRows(rows, COLS, { key: 'value', dir: 'desc' });
  if (!rows.length) return <Empty>{loading ? 'Loading…' : 'No positions in the register yet.'}</Empty>;

  return (
    <div className="scrollbar-thin -mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[680px] text-[13px]">
        <thead>
          <tr className="border-b border-gray-200 text-[9px] uppercase tracking-[2px] text-gray-400 dark:border-gray-700 dark:text-gray-500">
            <SortableTh label="Position" colKey="name" toggle={toggle} indicator={indicator} align="left" className="py-2 pr-2 font-normal" />
            <SortableTh label="Book" colKey="book" toggle={toggle} indicator={indicator} align="left" className="py-2 px-2 font-normal" />
            <SortableTh label="Stage" colKey="stage" toggle={toggle} indicator={indicator} align="left" className="py-2 px-2 font-normal" />
            <SortableTh label="Value" colKey="value" toggle={toggle} indicator={indicator} className="py-2 px-2 font-normal" />
            <SortableTh label="Free" colKey="free" toggle={toggle} indicator={indicator} className="py-2 px-2 font-normal" />
            {canWrite && <th className="py-2 pl-2 text-right font-normal">Advance</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => {
            const ix = PIPELINE.indexOf(p.inventory_status);
            const next = ix >= 0 && ix < PIPELINE.length - 1 ? PIPELINE[ix + 1] : null;
            // Sign-off is the Shareholder's act; offer the control only to someone who
            // can actually take it, rather than surfacing a button that returns 403.
            const canAdvance = next && (next !== 'signed_off' || canApprove);
            return (
              <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-2 pr-2">
                  <div className="text-gray-900 dark:text-gray-100">{p.name}</div>
                  {p.constraint_note && (
                    <div className="text-[11px] text-gray-400 dark:text-gray-500">{p.constraint_note}</div>
                  )}
                </td>
                <td className="px-2 text-gray-500 dark:text-gray-400">{p.book_display}</td>
                <td className="px-2"><Chip className={STAGE_CHIP[p.inventory_status]}>{p.inventory_display}</Chip></td>
                <td className="px-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{fmtM(p.carrying_value)}</td>
                <td className={`px-2 text-right tabular-nums ${p.free_value < p.carrying_value ? AVAIL.partial.text : AVAIL.free.text}`}>
                  {fmtM(p.free_value)}
                </td>
                {canWrite && (
                  <td className="pl-2 text-right">
                    {canAdvance ? (
                      <button
                        onClick={() => onTransition(p, next)}
                        className="whitespace-nowrap rounded border border-brand-gold/40 px-2 py-1 text-[11px] text-brand-gold hover:bg-brand-gold/10"
                      >
                        {next === 'signed_off' ? 'Sign off' : `Mark ${PIPELINE_LABEL[next].toLowerCase()}`}
                      </button>
                    ) : (
                      <span className="text-[11px] text-gray-400">
                        {next ? 'Shareholder' : '—'}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
